/**
 * P3-42: ReservesPanel —— 储备资源/小猫面板。
 *
 * 对齐 `js/challenges.js:953-1162`：
 * - 面板可见条件：reservesExist() || chronophysics.researched
 * - 40%/60% 双栏布局
 * - 资源表分 craftable/non-craftable 两段，仅 ascoh perk 精确显示
 * - 小猫表受 fractured pact 混淆
 */
import { useGameTick } from "../../bridge/useGameTick";
import { t } from "../../bridge/i18n";
import type { GamePage } from "../../bridge/GameBridge";
import { SpacePanel } from "../space/SpacePanel";
import ReclaimReservesBtn from "./ReclaimReservesBtn";

interface ResLike {
  name: string;
  title?: string;
  craftable?: boolean;
  type?: string;
  color?: string;
  style?: Record<string, string>;
}

interface KittenLike {
  name?: string;
  trait?: { title?: string };
  rank?: number;
}

interface ResourceRowSnapshot {
  title: string;
  amount: string;
  style: Record<string, string>;
  separatorBefore: boolean;
}

interface KittenRowSnapshot {
  name: string;
  trait: string;
  rankText: string;
  isEllipsis: boolean;
}

interface ReservesSnapshot {
  visible: boolean;
  summary: string;
  reclaimInstructionsVisible: boolean;
  instructions: string;
  resourceMode: "none" | "fuzzy" | "table";
  noneText: string;
  fuzzyLabel: string;
  fuzzyText: string;
  resourceRows: ResourceRowSnapshot[];
  cryochambersLabel: string | null;
  kittenRows: KittenRowSnapshot[];
  showKittens: boolean;
}

const MAX_KITTENS = 30;

function getResourceStyle(res: ResLike): Record<string, string> {
  const style: Record<string, string> = {};
  if (res.type === "uncommon") {
    style.color = "Coral";
  } else if (res.type === "rare") {
    style.color = "orange";
    style.textShadow = "1px 0px 10px Coral";
  } else if (res.color) {
    style.color = res.color;
  }
  if (res.style) {
    for (const key in res.style) {
      style[key] = res.style[key];
    }
  }
  return style;
}

function getFuzzyKey(numTypes: number): string {
  if (numTypes >= 30) {
    return "challendge.reserves.resources.30orMore";
  }
  if (numTypes >= 20) {
    return "challendge.reserves.resources.20orMore";
  }
  if (numTypes >= 10) {
    return "challendge.reserves.resources.10orMore";
  }
  return "challendge.reserves.resources.lessThan10";
}

function getSnapshot(game: GamePage | null): ReservesSnapshot | null {
  if (!game) {
    return null;
  }
  const reserves = game.challenges?.reserves;
  const hasReserves = reserves?.reservesExist?.() ?? false;
  const chronophysics = game.science?.get?.("chronophysics")?.researched ?? false;
  const visible = hasReserves || chronophysics;

  if (!visible) {
    return null;
  }

  const anyActive = game.challenges?.anyChallengeActive?.() ?? false;
  const instructionsVisible = hasReserves && (anyActive || game.ironWill);

  const reserveResources = (reserves?.reserveResources as Record<string, number> | undefined) ?? {};
  const reserveKittens = (reserves?.reserveKittens as KittenLike[] | undefined) ?? [];
  const reserveCryochambers = (reserves?.reserveCryochambers as number | undefined) ?? 0;

  const numTypes = Object.keys(reserveResources).length;

  let resourceMode: "none" | "fuzzy" | "table" = "none";
  const resourceRows: ResourceRowSnapshot[] = [];

  if (numTypes < 1) {
    resourceMode = "none";
  } else if (!(game.prestige?.getPerk?.("ascoh")?.researched ?? false)) {
    resourceMode = "fuzzy";
  } else {
    resourceMode = "table";
    const allRes = (game.resPool?.resources as ResLike[] | undefined) ?? [];
    const nonCraftableRows: ResourceRowSnapshot[] = [];
    const craftableRows: ResourceRowSnapshot[] = [];

    for (const res of allRes) {
      const amount = reserveResources[res.name] ?? 0;
      if (!amount) {
        continue;
      }
      if (res.craftable && res.name !== "wood") {
        continue;
      }
      nonCraftableRows.push({
        title: res.title || res.name + ":",
        amount: game.getDisplayValueExt?.(amount) ?? String(amount),
        style: getResourceStyle(res),
        separatorBefore: false,
      });
    }

    for (const res of allRes) {
      const amount = reserveResources[res.name] ?? 0;
      if (!amount) {
        continue;
      }
      if (!res.craftable || res.name === "wood") {
        continue;
      }
      craftableRows.push({
        title: res.title || res.name + ":",
        amount: game.getDisplayValueExt?.(amount) ?? String(amount),
        style: getResourceStyle(res),
        separatorBefore: false,
      });
    }

    if (nonCraftableRows.length > 0 && craftableRows.length > 0) {
      craftableRows[0].separatorBefore = true;
    }

    resourceRows.push(...nonCraftableRows, ...craftableRows);
  }

  const cryochambersLabel = reserveCryochambers > 0
    ? t("challendge.reserves.cryochambers.label", [reserveCryochambers])
    : null;

  const ascoh = game.prestige?.getPerk?.("ascoh")?.researched ?? false;
  const fractured =
    Boolean(game.religion?.getPact?.("fractured")?.val) &&
    Boolean(game.getFeatureFlag?.("MAUSOLEUM_PACTS"));

  const kittenRows: KittenRowSnapshot[] = [];
  const showKittens = reserveKittens.length > 0 && ascoh;
  if (showKittens) {
    for (let i = 0; i < reserveKittens.length; i += 1) {
      if (i >= MAX_KITTENS) {
        kittenRows.push({
          name: "...",
          trait: "...",
          rankText: "",
          isEllipsis: true,
        });
        break;
      }
      const kitten = reserveKittens[i];
      let traitLabel = kitten.trait?.title ?? "";
      let rank = kitten.rank ?? 0;
      if (fractured) {
        traitLabel = "???";
        rank = (game.rand?.(4) ?? 0) * 2;
        if ((game.rand?.(100) ?? 0) < 25) {
          rank += 1;
        }
      }
      const rankText =
        rank === 0 ? "" : " (" + t("village.census.rank") + " " + rank + ")";
      kittenRows.push({
        name:
          game.village?.getStyledName?.(kitten, false) ??
          String(kitten.name ?? ""),
        trait: traitLabel,
        rankText,
        isEllipsis: false,
      });
    }
  }

  return {
    visible: true,
    summary: t("challendge.reserves.panel.summary"),
    reclaimInstructionsVisible: instructionsVisible,
    instructions: t("challendge.reserves.panel.reclaim.instructions"),
    resourceMode,
    noneText: t("challendge.reserves.resources.none"),
    fuzzyLabel: t("challendge.reserves.resources.label"),
    fuzzyText: resourceMode === "fuzzy" ? t(getFuzzyKey(numTypes)) : "",
    resourceRows,
    cryochambersLabel,
    kittenRows,
    showKittens,
  };
}

function snapEq(a: ReservesSnapshot | null, b: ReservesSnapshot | null): boolean {
  if (a === b) {
    return true;
  }
  if (!a || !b) {
    return false;
  }
  if (
    a.visible !== b.visible ||
    a.summary !== b.summary ||
    a.reclaimInstructionsVisible !== b.reclaimInstructionsVisible ||
    a.instructions !== b.instructions ||
    a.resourceMode !== b.resourceMode ||
    a.noneText !== b.noneText ||
    a.fuzzyLabel !== b.fuzzyLabel ||
    a.fuzzyText !== b.fuzzyText ||
    a.cryochambersLabel !== b.cryochambersLabel ||
    a.showKittens !== b.showKittens ||
    a.resourceRows.length !== b.resourceRows.length ||
    a.kittenRows.length !== b.kittenRows.length
  ) {
    return false;
  }
  for (let i = 0; i < a.resourceRows.length; i++) {
    const ar = a.resourceRows[i];
    const br = b.resourceRows[i];
    if (
      ar.title !== br.title ||
      ar.amount !== br.amount ||
      ar.separatorBefore !== br.separatorBefore
    ) {
      return false;
    }
    const aStyleKeys = Object.keys(ar.style);
    const bStyleKeys = Object.keys(br.style);
    if (aStyleKeys.length !== bStyleKeys.length) {
      return false;
    }
    for (const key of aStyleKeys) {
      if (ar.style[key] !== br.style[key]) {
        return false;
      }
    }
  }
  for (let i = 0; i < a.kittenRows.length; i++) {
    const ak = a.kittenRows[i];
    const bk = b.kittenRows[i];
    if (
      ak.name !== bk.name ||
      ak.trait !== bk.trait ||
      ak.rankText !== bk.rankText ||
      ak.isEllipsis !== bk.isEllipsis
    ) {
      return false;
    }
  }
  return true;
}

export default function ReservesPanel() {
  const snapshot = useGameTick(
    (game) => getSnapshot(game as GamePage | null),
    snapEq,
  );

  if (!snapshot || !snapshot.visible) {
    return null;
  }

  return (
    <SpacePanel title="challendge.reserves.panel.label">
      <span style={{ display: "inline-block", marginBottom: "16px" }}>
        {snapshot.summary}
      </span>

      <ReclaimReservesBtn />

      {snapshot.reclaimInstructionsVisible && (
        <span style={{ display: "block", marginBottom: "16px" }}>
          {snapshot.instructions}
        </span>
      )}

      <table>
        <tbody>
          <tr>
            <td
              className="craftStuffPanel"
              style={{ width: "40%", verticalAlign: "top" }}
            >
              {snapshot.resourceMode === "none" && (
                <span>{snapshot.noneText}</span>
              )}
              {snapshot.resourceMode === "fuzzy" && (
                <span>
                  {snapshot.fuzzyLabel} {snapshot.fuzzyText}
                </span>
              )}
              {snapshot.resourceMode === "table" && (
                <table>
                  <tbody>
                    {snapshot.resourceRows.map((row, index) => (
                      <ResourceRow key={`${row.title}-${index}`} row={row} />
                    ))}
                  </tbody>
                </table>
              )}
            </td>
            <td
              className="craftStuffPanel"
              style={{ width: "60%", verticalAlign: "top" }}
            >
              {snapshot.cryochambersLabel && (
                <span>{snapshot.cryochambersLabel}</span>
              )}
              {snapshot.showKittens && (
                <table>
                  <tbody>
                    {snapshot.kittenRows.map((row, index) => (
                      <tr key={`kitten-${index}`}>
                        <td style={{ paddingRight: "8px" }}>{row.name}</td>
                        {!row.isEllipsis && (
                          <>
                            <td style={{ whiteSpace: "nowrap", overflow: "clip" }}>
                              {row.trait}
                            </td>
                            <td style={{ whiteSpace: "nowrap", overflow: "clip" }}>
                              {row.rankText}
                            </td>
                          </>
                        )}
                        {row.isEllipsis && (
                          <>
                            <td>...</td>
                            <td>...</td>
                          </>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </td>
          </tr>
        </tbody>
      </table>
    </SpacePanel>
  );
}

function ResourceRow({ row }: { row: ResourceRowSnapshot }) {
  return (
    <>
      {row.separatorBefore && (
        <tr>
          <td colSpan={2}>
            <hr />
          </td>
        </tr>
      )}
      <tr>
        <td style={row.style}>{row.title}</td>
        <td>{row.amount}</td>
      </tr>
    </>
  );
}
